import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { msalInstance } from '../../auth/msalConfig.js';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

/**
 * Delegated Graph scope needed to update the Exchange-backed group properties.
 * Microsoft blocks app-only (client-credential) updates for these, so the PATCH must run
 * with the signed-in member's own token.
 */
const SCOPES = ['Group.ReadWrite.All'];

export interface GroupSettingsResult {
  ok: boolean;
  /** Human-readable failure reason when `ok` is false. */
  message: string | null;
}

/**
 * Enables "allow external senders" (`allowExternalSenders`) and "send copies of group
 * conversations and events to members" (`autoSubscribeNewMembers`) on a newly created
 * Microsoft 365 group. Run BEFORE members are added so every member is auto-subscribed
 * on add (no Exchange PowerShell backfill needed). Retries briefly on 404 because a new
 * group replicates with a short delay.
 */
export async function applyGroupMailSettings(groupId: string): Promise<GroupSettingsResult> {
  if (!msalInstance) {
    return { ok: false, message: 'Sign-in is not configured in this environment.' };
  }
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return { ok: false, message: 'No signed-in Microsoft account was found.' };
  }

  let accessToken: string;
  try {
    accessToken = (await msalInstance.acquireTokenSilent({ scopes: SCOPES, account })).accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      try {
        accessToken = (await msalInstance.acquireTokenPopup({ scopes: SCOPES, account }))
          .accessToken;
      } catch {
        return {
          ok: false,
          message:
            'Permission to update group settings was not granted (popup blocked or consent declined).',
        };
      }
    } else {
      return { ok: false, message: 'Could not get a Microsoft Graph token.' };
    }
  }

  const backoffsMs = [0, 2000, 5000, 8000];
  let lastDetail = '';
  for (const wait of backoffsMs) {
    if (wait) await new Promise((resolve) => setTimeout(resolve, wait));
    let res: Response;
    try {
      res = await fetch(`${GRAPH_BASE}/groups/${encodeURIComponent(groupId)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowExternalSenders: true, autoSubscribeNewMembers: true }),
      });
    } catch {
      lastDetail = 'Microsoft Graph was unreachable.';
      continue;
    }
    if (res.ok) return { ok: true, message: null };
    let detail = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      detail = json.error?.message || detail;
    } catch {
      // Keep the status-code detail.
    }
    lastDetail = detail;
    // Retry only on 404 (new-group replication delay); other errors are terminal.
    if (res.status !== 404) break;
  }
  return { ok: false, message: `Microsoft Graph rejected the update: ${lastDetail}` };
}
